import { useState, useEffect } from 'react';
import { useAuth } from '../../auth';

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { getToken, isLoaded } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<number | null>(null);

  useEffect(() => {
    async function checkAdminStatus() {
      try {
        const token = await getToken();
        const response = await fetch('/api/admin/status', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        setStatusCode(response.status);
        setIsAdmin(response.ok);
        if (!response.ok) {
          const data = await response.json().catch(() => null);
          setAccessError(data?.error ?? `Admin check failed with ${response.status}`);
        } else {
          setAccessError(null);
        }
      } catch {
        setIsAdmin(false);
        setStatusCode(null);
        setAccessError('Could not reach the local API at /api/admin/status.');
      } finally {
        setIsChecking(false);
      }
    }

    if (isLoaded) {
      checkAdminStatus();
    }
  }, [isLoaded, getToken]);

  if (!isLoaded || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-text-dim">Checking access...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg px-6">
        <div className="w-full max-w-xl rounded-lg border border-line bg-panel p-6">
          <div className="text-[10px] font-mono uppercase text-text-faint">Admin access</div>
          <h1 className="mt-2 text-xl font-display font-medium text-text">Could not open admin</h1>
          <p className="mt-2 text-sm text-text-dim">
            {accessError ?? 'Your session is not authorized for the admin area.'}
          </p>

          <div className="mt-4 rounded-lg border border-line bg-bg p-4 text-sm text-text-dim">
            <div className="font-medium text-text">Localhost checklist</div>
            <ul className="mt-2 space-y-1">
              <li>API server is running on the Vite proxy target, usually <code>localhost:3001</code>.</li>
              <li>Your email address is included in <code>ADMIN_EMAILS</code> for the API process.</li>
              <li>If using the local auth bypass, both <code>E2E_AUTH_BYPASS</code> and <code>VITE_E2E_AUTH_BYPASS</code> must be enabled.</li>
            </ul>
            {statusCode && (
              <div className="mt-3 text-xs text-text-faint">Admin status response: {statusCode}</div>
            )}
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <a href="/" className="btn btn-sm">Back to site</a>
            <button type="button" onClick={() => window.location.reload()} className="btn btn-accent btn-sm">
              Recheck access
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
